import { Link } from '@tanstack/router'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { COUNTRY_CODE } from '../../constants/config'
import { COUNTRY_CODE_MAP } from '../../constants/countries'
import useGameCategories from '../../hooks/useGameCategories'
import { useBoundStore } from '../../store/store'
import type { ApiResponse } from '../../types/api'
import type { FooterLinks as FooterLinksData } from '../../types/common'
import { api } from '../../utils/api-client'
import classNames from '../../utils/classNames'

// const SocialMedia = styled.div`
//   gap: 12px;
//   margin-top: 8px;
//   img {
//     max-width: 27px;
//     max-height: 27px;
//   }
//   a,
//   div {
//     margin: 0;
//     display: flex;
//     align-items: center;
//     justify-content: center;
//     height: 48px;
//   }
//   @media (min-width: 1200px) {
//     margin-top: 14px;
//     img {
//       max-width: 32px;
//       max-height: 32px;
//     }
//   }
// `

export const FooterTitle = ({ title }: { title: string }) => (
  <p className="block text-left text-sm font-semibold text-highlight uppercase mb-3 md:text-lg xl:mb-3">
    {title}
  </p>
)

export const FooterLink = ({
  title,
  to,
  slug,
}: {
  title: string
  to?: '/promotions' | '/sponsors' | '/contact-us'
  slug?: string
}) => {
  return to ? (
    <Link
      to={to}
      className="block text-highlight text-xs text-left font-light mb-3 transition-all duration-[0.2s] ease-[ease-in-out] md:text-base xl:mb-2.5 hover:opacity-70 last:mb-0">
      {title}
    </Link>
  ) : (
    <Link
      to="/help/$page"
      params={{ page: slug as string }}
      className="block text-highlight text-xs text-left font-light mb-3 transition-all duration-[0.2s] ease-[ease-in-out] md:text-base xl:mb-2.5 hover:opacity-70 last:mb-0">
      {title}
    </Link>
  )
}

const FooterLinks = () => {
  const { t } = useTranslation(['drawer', 'footer', 'casino'])
  const pending_bind = useBoundStore(state => state.user.pending_bind)
  const setIsPendingDepositVisible = useBoundStore(
    state => state.common.setIsPendingDepositVisible,
  )
  const { categories } = useGameCategories()
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)

  const footerLinks = useBoundStore(state => state.common.footerLinks)
  const setFooterLinks = useBoundStore(state => state.common.setFooterLinks)

  const toggleDropdown = () => {
    setIsDropdownOpen(prev => !prev)
  }

  useEffect(() => {
    const controller = new AbortController()
    const signal = controller.signal

    const getFooterLinks = async () => {
      try {
        const response = await api.post<ApiResponse<FooterLinksData>>(
          '/footer.php',
          { signal },
        )
        const { status, info } = response.data

        if (status === 1) {
          setFooterLinks(info)
        }
      } catch (error) {
        console.error(error)
      }
    }

    if (
      !Object.keys(footerLinks).length &&
      COUNTRY_CODE === COUNTRY_CODE_MAP.EC
    )
      getFooterLinks()

    return () => {
      controller.abort()
    }
  }, [])

  const sections = useMemo(() => {
    return {
      'information':
        COUNTRY_CODE === COUNTRY_CODE_MAP.EC
          ? footerLinks.information ?? []
          : [
              {
                slug: 'we',
                title: t('about-us', { ns: 'footer' }),
              },
              {
                slug: 'terminos-y-condiciones',
                title: t('terms-and-conditions', { ns: 'footer' }),
              },
              {
                slug: 'juego-responsable',
                title: t('responsible-gaming', { ns: 'footer' }),
              },
              {
                slug: 'privacy-notice-and-site-cookies',
                title: t('privacy-notice-and-site-cookies', { ns: 'footer' }),
              },
              {
                slug: 'affiliate-program',
                title: t('affiliate-program', { ns: 'footer' }),
              },
              {
                slug: 'betting-mechanics',
                title: t('betting-mechanics', { ns: 'footer' }),
              },
              {
                slug: 'betting-conditions',
                title: t('betting-conditions', { ns: 'footer' }),
              },
            ],
      'help':
        COUNTRY_CODE === COUNTRY_CODE_MAP.EC
          ? footerLinks.ayuda ?? []
          : [
              {
                slug: 'faq',
                title: t('faqs', { ns: 'footer' }),
              },
              {
                slug: 'deposit',
                title: t('deposit', { ns: 'footer' }),
              },
              {
                slug: 'withdraw',
                title: t('withdraw', { ns: 'footer' }),
              },
              {
                slug: 'refund-policy',
                title: t('refund-policy', { ns: 'footer' }),
              },
            ],
    }
  }, [footerLinks])

  const gamesLink = useCallback(() => {
    return categories
      .filter(c => c.id !== 'favorites' && c.id !== 'recent_played')
      .map(category => (
        <Link
          key={category.id}
          to="/casino/$category"
          params={{
            category: category.category.toLowerCase().replace(' ', '-'),
          }}
          className="no-underline capitalize last:mb-2 block text-highlight text-xs text-left font-light mb-3 transition-all duration-[0.2s] ease-[ease-in-out] xl:text-base xl:mb-2.5 hover:opacity-70">
          {category.category.toLowerCase()}
        </Link>
      ))
  }, [categories])

  return (
    <div className="grid grid-cols-[1fr_1fr] gap-5 mb-[50px] px-5 py-0 xl:w-[1200px] xl:flex xl:justify-between xl:grid-cols-4 xl:mt-0 xl:mb-10 3xl:p-0">
      <div>
        <FooterTitle title={t('information', { ns: 'footer' })} />
        {sections['information'].map((i, index) => (
          <FooterLink key={index} {...i} />
        ))}
      </div>
      <div>
        <FooterTitle title={t('our-sections', { ns: 'footer' })} />
        <Link
          to={
            +pending_bind === 0 || +pending_bind === 2
              ? undefined
              : '/sports/bti'
          }
          onClick={() => {
            if (+pending_bind === 0 || +pending_bind === 2)
              setIsPendingDepositVisible(true)
          }}
          className="block text-highlight text-xs text-left font-light mb-3 transition-all duration-[0.2s] ease-[ease-in-out] md:text-base xl:mb-2.5 hover:opacity-70">
          {t('sports', { ns: 'drawer' })}
        </Link>
        <button
          onClick={toggleDropdown}
          className="block text-highlight text-xs text-left font-light mb-3 transition-all duration-[0.2s] ease-[ease-in-out] md:text-base xl:mb-2.5 hover:opacity-70">
          {t('casino:games')}
        </button>
        <div
          className={classNames(
            'overflow-hidden transition-all duration-[0.3s]',
            !isDropdownOpen && 'h-0',
          )}>
          {gamesLink()}
        </div>
        <FooterLink
          to="/promotions"
          title={t('promotions', { ns: 'drawer' })}
        />
        <FooterLink to="/sponsors" title={t('sponsors', { ns: 'drawer' })} />
      </div>
      <div>
        <FooterTitle title={t('help', { ns: 'footer' })} />
        {sections['help'].map((i, index) => (
          <FooterLink key={index} {...i} />
        ))}
      </div>
      <div>
        <FooterTitle title={t('contact-us', { ns: 'footer' })} />
        <FooterLink to="/contact-us" title={t('footer:contact-us')} />
      </div>
      {/* <a href="mailto:info@fun88.com" target="_blank" rel="noreferrer">
          {t('email')}: info@fun88.com
        </a>
        <p>{t('phone')}: +56 553 398 16 92</p> */}
      {/* <SocialMedia className="flx-str-ctr">
          <a
            aria-label="instagram page"
            href="https://www.instagram.com/fun88.com/"
            target="blank">
            <Icon id="instagram" width="36" height="36" />
          </a>
          <a
            aria-label="twitter page"
            href="https://twitter.com/mexplay_mx"
            target="blank">
            <Icon id="twitter" width="39" height="42" />
          </a>
          <a
            aria-label="facebook-page"
            href="https://www.facebook.com/fun88.com"
            target="blank">
            <Icon id="facebook-f" width="20" height="36" />
          </a>
          <div>
            <Icon id="18-plus" width="38" height="38" />
          </div>
        </SocialMedia> */}
    </div>
  )
}

export default FooterLinks
